import { PUBLIC_APEX } from '@/config/public-host';

/**
 * True when the SPA is being served on a user-owned domain (PURCHASED or
 * CUSTOM) rather than on our apex / its subdomains / localhost.
 *
 * Used by the public router to decide whether to bootstrap via
 * `/public/resolve` instead of relying on URL-encoded project slugs.
 */
/** Normalize a hostname: lowercase + strip a single trailing dot (FQDN form
 * `host.` is equivalent to `host`). Keeps isCustomDomain/isOurApexHost strict
 * complements on trailing-dot inputs (WEB-395 cto-duo F-1). */
function normalizeHost(host: string): string {
  return host.toLowerCase().replace(/\.$/, '');
}

export function isCustomDomain(): boolean {
  if (typeof window === 'undefined') return false;
  const host = normalizeHost(window.location.hostname);
  if (host === 'localhost' || host === '127.0.0.1') return false;
  // DEV-ONLY (tunnel): хост cloudflared — не пользовательский домен, иначе SPA
  // уходит в публичный резолв вместо кабинета. НЕ коммитить.
  if (host.endsWith('.trycloudflare.com')) return false;
  const apex = PUBLIC_APEX.toLowerCase();
  if (host === apex) return false;
  if (host.endsWith(`.${apex}`)) return false;
  return true;
}

/**
 * WEB-490: first path segments claimed by the authenticated dashboard/auth
 * flow on the apex host (see router/index.tsx's apexRouter — auth pages,
 * DashboardLayout children, and the /admin, /invite, /handoff literal
 * routes). react-router scores these literal segments above the
 * `/:quizSlug` / `/:projectId/:quizSlug` public-player catch-alls, so a
 * project id / quiz / paywall slug can never equal one of these words in
 * practice.
 *
 * Hand-maintained (mirrors the RESERVED_SUBDOMAINS pattern in
 * extractProjectSlug.ts) rather than imported from the router, to avoid a
 * circular import (router/index.tsx already imports this module). If a new
 * literal top-level route is added to apexRouter, add its first path segment
 * here too — otherwise a visit to that page would be wrongly treated as a
 * public-player route by {@link isPublicPlayerContext} and would force the
 * light theme onto the authenticated cabinet.
 */
const RESERVED_APEX_PATH_SEGMENTS = new Set([
  'login',
  'register',
  'reset-password',
  'verify-email',
  'funnels',
  'quizzes',
  'paywalls',
  'leads',
  'analytics',
  'dashboard',
  'experiments',
  'domains',
  'email-sequences',
  'settings',
  'project',
  'statistics',
  'smart-links',
  // WEB-2013 (строка 7): канонический адрес раздела «A/B-тесты»; прежний
  // 'smart-links' остаётся перенаправлением и потому тоже адрес кабинета.
  'ab-tests',
  'commerce',
  'projects',
  'org',
  'admin',
  'invite',
  'handoff',
  'health',
  'manage',
  // WEB-806: раздел «Контент» (WEB-647) не был внесён при добавлении роута —
  // кабинет на /content принимался за публичный плеер и терял тёмную тему
  // при перезагрузке (publicSafeHtmlTheme принудительно светлит плеер).
  'content',
  'kit-demo',
  // WEB-3310: витрина кита кабинета нового вида /kit (только вне боя).
  'kit',
  // WEB-1336 (П5): третий повтор той же ошибки (после content и kit-demo) —
  // ai-studio и audit добавлены в роутер коммитами b95bdb04/1626131d ПОСЛЕ
  // последнего изменения этого списка, кабинет на них принудительно светлел
  // и терял русскую локаль. Ручная синхронизация не работает — теперь список
  // сверяется с роутером сторожевым тестом publicHost.routerGuard.web1336.
  'ai-studio',
  'audit',
  // WEB-1288: наши собственные юридические документы («Условия использования»
  // и «Политика конфиденциальности»), которые человек принимает при
  // регистрации. Это страницы кабинета на апексе, а не публичный плеер: без
  // записи здесь визит на /terms принудительно светлил бы тему и сбрасывал
  // русскую локаль — та же беда, что была у content, kit-demo, ai-studio.
  // Обратная сторона — слова 'terms' и 'privacy' зарезервированы: квиз с
  // ТОЧНО таким слагом на апексе больше не откроется (слаг вида 'terms-2024'
  // не задет).
  'terms',
  'privacy',
  // WEB-2162 п.1: отдельный документ «Данные о здоровье». Сегмент нужен СВОЙ,
  // хотя выше уже есть 'health': сравнение идёт по целому сегменту адреса, и
  // 'health' слово 'health-data' не покрывает.
  'health-data',
  // WEB-2312: перенаправление старого адреса «Пополнить» (/billing) на
  // страницу «Баланс». Слаг ровно 'billing' на апексе больше не откроется.
  'billing',
  // WEB-3748 юнит 2: главная проекта (дашборд) /home. Слаг ровно 'home' на
  // апексе больше не откроется (второй конец — RESERVED_APEX_SLUGS сервера).
  'home',
]);

/**
 * WEB-1336 (П5): exported ONLY for the router-guard test
 * (publicHost.routerGuard.web1336.test.ts), which cross-checks every literal
 * top-level route segment in router/index.tsx against this list. Not for
 * production use — call {@link isPublicPlayerContext} instead.
 */
export const RESERVED_APEX_PATH_SEGMENTS_FOR_TEST: ReadonlySet<string> =
  RESERVED_APEX_PATH_SEGMENTS;

/** True when the URL query string carries the editor's draft-preview flag. */
function isPreviewQuery(search: string): boolean {
  return new URLSearchParams(search).get('preview') === '1';
}

/**
 * True when `pathname` shapes like a public-player catch-all route
 * (PublicQuiz / PublicPaywall) rather than a reserved dashboard/auth path.
 * See {@link RESERVED_APEX_PATH_SEGMENTS}. Deliberately conservative: any
 * shape it doesn't recognize returns false (never over-forces the player
 * context onto an unrecognized dashboard route).
 */
function isPlayerRoutePath(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return false;

  if (segments.length === 1) {
    return !RESERVED_APEX_PATH_SEGMENTS.has(segments[0]);
  }

  if (segments.length === 2) {
    // '/paywall/:paywallSlug' — literal, never a reserved dashboard word.
    if (segments[0] === 'paywall') return true;
    // '/:projectId/:quizSlug'
    return !RESERVED_APEX_PATH_SEGMENTS.has(segments[0]);
  }

  if (segments.length === 3) {
    // '/:projectId/paywall/:paywallSlug'
    return segments[1] === 'paywall';
  }

  return false;
}

/**
 * WEB-490 (QA bounce off 793cfba): true when the SPA is rendering — or is
 * about to render — the public, unauthenticated quiz/paywall player, where a
 * visitor's OS dark-mode preference must never leak into `data-theme` (see
 * publicSafeHtmlTheme). The previous fix forced light ONLY on
 * `isCustomDomain()`, missing the apex host / its subdomains / localhost —
 * where the player also renders via the editor's draft preview (`?preview=1`)
 * or legacy-path-mode dev URLs (e.g. `/<quizSlug>`, `/<projectId>/<quizSlug>`,
 * `/paywall/<slug>`).
 *
 * False for the authenticated cabinet/dashboard, which must keep following
 * the user's own resolved theme (system/light/dark) — see
 * {@link isPlayerRoutePath} for how that boundary is drawn.
 */
export function isPublicPlayerContext(): boolean {
  if (typeof window === 'undefined') return false;
  if (isCustomDomain()) return true;
  if (isPreviewQuery(window.location.search)) return true;
  return isPlayerRoutePath(window.location.pathname);
}

/**
 * WEB-395 (SCR 2026-06-30): funnels render ONLY on user-owned custom domains.
 * True ONLY for our own apex host and its subdomains (`<slug>.<apex>`), which
 * must NOT serve funnels — the public renderer 404s there. Distinct from
 * `!isCustomDomain()`: localhost / preview hosts are NOT our apex, so local dev
 * preview of the public renderer keeps working.
 */
export function isOurApexHost(): boolean {
  if (typeof window === 'undefined') return false;
  const host = normalizeHost(window.location.hostname);
  const apex = PUBLIC_APEX.toLowerCase();
  return host === apex || host.endsWith(`.${apex}`);
}
